import { memo } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { appointmentDraftSelectors } from '~/api';
import { formatPrice } from '~/utils';

import { setEditMode } from '~/routes/edit';
import { envConfigSelectors } from '~/components/configuration/configuration.selectors';

import { getEstimationTimeForAllProducts } from '~/ocab-services/measure/utils';

import { Section } from '../section';
import { SectionItem } from '../section-item';
import { AddressDetails } from './address-details';
import { DateTimeDetails } from './date-time-details';
import { ShopDetails } from './shop-details';

export const MainDetails = memo(
  ({
    appointmentReview: {
      appointmentType: { name: appointmentName },
      shop,
      dateSlot,
      timeSlot,
    },
    address = '',
    selectedPartner = {},
    partnerTitle = '',
    shopTitle = 'Shop',
    price,
    formatTimeSlot = null,
    displayAppointmentType,
    displayPartner,
    displayShopDetails,
    hideShopAddress,
    history,
    match: { params: matchParams } = {},
    firstEditPagePath,
    hideEditButton,
    remoteShopAddress,
    appointmentEstimationDuration,
    mainDetailsConfig: { addressTitle } = {},
  }) => (
    <Section
      category="Appointment Details"
      history={history}
      matchParams={matchParams}
      editPath={firstEditPagePath}
      analyticsSectionType="appointments"
      showEditButton={!hideEditButton}
    >
      {displayAppointmentType && (
        <SectionItem title="Appointment type" definition={appointmentName} />
      )}
      {displayPartner && (
        <SectionItem title={partnerTitle} definition={selectedPartner.name} dataTest="partner" />
      )}
      {address && <AddressDetails title={addressTitle ?? 'Your Address'} address={address} />}
      {displayShopDetails && (
        <ShopDetails
          shop={shop}
          title={shopTitle}
          remoteShopAddress={remoteShopAddress}
          hideShopAddress={hideShopAddress}
        />
      )}
      <DateTimeDetails
        title="Date and time"
        selectedDateSlot={dateSlot}
        selectedTimeSlot={timeSlot}
        formatTimeSlot={formatTimeSlot}
      >
        {appointmentEstimationDuration && <p>{appointmentEstimationDuration}</p>}
      </DateTimeDetails>
      <SectionItem title="Price" definition={formatPrice(price)} />
    </Section>
  ),
);

MainDetails.displayName = 'MainDetails';

export const mapStateToProps = (state) => {
  const {
    appointment: {
      appointment: { partner: selectedPartner, price, answers },
      draft: appointmentReview,
    },
    services: {
      config: { partnerTitle, ReviewForm = {}, ReviewDateTimeDetails = {} },
    },
  } = state;
  const shopTitle = envConfigSelectors.reviewFormShopDetails(appointmentReview.appointmentType.id)(
    state,
  );
  const products = appointmentDraftSelectors.selectProducts(state);
  const serviceConfig = envConfigSelectors.serviceConfig(appointmentReview.appointmentType.id)(
    state,
  );
  const config = envConfigSelectors.selectConfig(appointmentReview.appointmentType.id)(state);

  return {
    selectedPartner,
    formatTimeSlot: config?.ReviewForm?.formatTimeSlot,
    appointmentReview,
    partnerTitle,
    price,
    address: envConfigSelectors.selectUserAddress(
      serviceConfig,
      answers,
      appointmentReview.appointmentType.id,
    ),
    shopTitle,
    displayAppointmentType: !ReviewForm.hideAppointmentType,
    displayPartner: !!envConfigSelectors.selectChosenPartner(appointmentReview.appointmentType.id)(
      state,
    ),
    displayShopDetails: !envConfigSelectors.hideShopDetails(appointmentReview.appointmentType.id)(
      state,
    ),
    hideShopAddress: envConfigSelectors.hideShopAddress(appointmentReview.appointmentType.id)(
      state,
    ),
    firstEditPagePath: envConfigSelectors.selectFirstEditPage(appointmentReview.appointmentType.id)(
      state,
    ),
    hideEditButton: !!ReviewForm.hideEditButton,
    remoteShopAddress: envConfigSelectors.remoteShopAddress(appointmentReview.appointmentType.id)(
      state,
    ),
    appointmentEstimationDuration:
      ReviewDateTimeDetails.buildAppointmentEstimationDurationDescription &&
      ReviewDateTimeDetails.buildAppointmentEstimationDurationDescription(
        getEstimationTimeForAllProducts(products),
      ),
    mainDetailsConfig: envConfigSelectors.selectMainDetailsConfig(
      appointmentReview.appointmentType.id,
    )(state),
  };
};

export const mapStateToPropsManageAppointment = (state) => {
  const {
    appointment: {
      appointment: { answers },
      draft: appointmentReview,
    },
    partners: { partners },
    shops: { shops },
    services: {
      config: { chosenPartner, partnerTitle, ReviewForm = {} },
    },
    selectDate: { filters },
  } = state;
  const serviceConfig = envConfigSelectors.serviceConfig(appointmentReview.appointmentType.id)(
    state,
  );

  return {
    appointmentReview,
    partners,
    filters,
    shops,
    selectedPartner: appointmentReview.partner,
    price: appointmentReview.price,
    formatTimeSlot: ReviewForm.formatTimeSlot,
    partnerTitle,
    shopTitle: ReviewForm.shopDetails && ReviewForm.shopDetails.title,
    displayAppointmentType: !ReviewForm.hideAppointmentType,
    displayPartner: !!chosenPartner,
    displayShopDetails: !ReviewForm.hideShopDetails,
    address: envConfigSelectors.selectUserAddress(
      serviceConfig,
      answers,
      appointmentReview.appointmentType.id,
    ),
    remoteShopAddress: envConfigSelectors.remoteShopAddress(appointmentReview.appointmentType.id)(
      state,
    ),
    firstEditPagePath: envConfigSelectors.selectFirstEditPage(appointmentReview.appointmentType.id)(
      state,
    ),
  };
};

const mapDispatchToProps = {
  dispatchSetEditMode: setEditMode,
};

export const ConnectedMainDetails = connect(mapStateToProps, mapDispatchToProps)(MainDetails);

export const ConnectedMainDetailsManageAppointment = withRouter(
  connect(mapStateToPropsManageAppointment, mapDispatchToProps)(MainDetails),
);

MainDetails.propTypes = {
  history: PropTypes.shape({}).isRequired,
  address: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  remoteShopAddress: PropTypes.string,
  appointmentReview: PropTypes.shape({
    appointmentType: PropTypes.shape({}).isRequired,
    dateSlot: PropTypes.string.isRequired,
    shop: PropTypes.shape({}).isRequired,
    timeSlot: PropTypes.shape({}).isRequired,
  }).isRequired,
  selectedPartner: PropTypes.shape({
    name: PropTypes.string,
  }),
  partnerTitle: PropTypes.string,
  price: PropTypes.string.isRequired,
  displayAppointmentType: PropTypes.bool.isRequired,
  displayPartner: PropTypes.bool.isRequired,
  displayShopDetails: PropTypes.bool.isRequired,
  firstEditPagePath: PropTypes.string.isRequired,
  appointmentEstimationDuration: PropTypes.string,
  mainDetailsConfig: PropTypes.shape({
    addressTitle: PropTypes.string,
  }),

  formatTimeSlot: PropTypes.func,
  shopTitle: PropTypes.string,
  match: PropTypes.shape({}),
  hideEditButton: PropTypes.bool.isRequired,
};
